import React, { useState, useEffect } from "react"
import { Route, Routes as RouterRoutes, Navigate, useNavigate, useParams } from "react-router"
import AuthenticatedRoute from "./AuthenticatedRoute"
import { useTranslation } from "react-i18next"
import { translationFilenames } from "../i18n"
import LiveMeetingPage from "../../pages/liveMeeting/LiveMeetingPage"
import UserNewPage from "../../pages/user/UserNewPage"
import UserNewGuestPage from "../../pages/user/UserNewGuestPage"
import MeetingPage from "../../pages/meeting/MeetingPage"
import MeetingsPage from "../../pages/meeting/MeetingsPage"
import SignInPage from "../../pages/account/SignInPage"
import NewMeetingPage from "../../pages/meeting/MeetingNewPage"
import EditMeetingPage from "../../pages/meeting/MeetingEditPage"
import UsersPage from "../../pages/user/UsersPage"
import UserEditPage from "../../pages/user/UserEditPage"
import SetPasswordPage from "../../pages/account/SetPasswordPage"
import CasesPage from "../../pages/case/CasesPage"
import CasePage from "../../pages/case/CasePage"
import CaseEditPage from "../../pages/case/CaseEditPage"
import MeetingCasePage from "../../pages/meetingCase/MeetingCasePage"
import NotificationsPage from "../../pages/notification/NotificationsPage"
import TermsPage from "../../pages/account/TermsPage"
import AdminPage from "../../pages/admin/AdminPage"
import AppLayout from "../../layouts/AppLayout"
import AccountLayout from "../../layouts/AccountLayout"
import TermsLayout from "../../layouts/TermsLayout"
import SetupTotpCompletePage from "../../pages/account/SetupTotpCompletePage"
import MfaTotpPage from "../../pages/account/MfaTotpPage"
import MfaPage from "../../pages/account/MfaPage"
import SetupSmsCompletePage from "../../pages/account/SetupSmsCompletePage"
import TasksPage from "../../pages/task/TasksPage"
import PatientsPage from "../../pages/patients/PatientsPage"
import PathwaysPage from "../../pages/pathways/PathwaysPage"
import PathwayPage from "../../pages/pathways/PathwayPage"
import PathwayNewPage from "../../pages/pathways/PathwaysNewPage"
import PatientPage from "../../pages/patients/PatientPage"
import PatientNewPage from "../../pages/patients/PatientNewPage"
import PatientCaseNewPage from "../../pages/patients/PatientCaseNewPage"
import PatientEditDetailsPage from "../../pages/patients/PatientEditDetailsPage"
import PatientEditFurtherDetailsPage from "../../pages/patients/PatientEditFurtherDetailsPage"
import TeamsPage from "../../pages/teams/TeamsPage"
import TeamsNewPage from "../../pages/teams/TeamsNewPage"
import TeamPage from "../../pages/teams/TeamPage"
import TeamEditPage from "../../pages/teams/TeamEditPage"
import { IAppState } from "../appTypes"
import { useSelector } from "react-redux"
import MessagesPage from "../../pages/messages/MessagesPage"
import { getCurrentUser } from "../../shared/selectors/user"
import { isGuest } from "../../shared/helpers/userHelpers"
import AnalyticsPage from "../../pages/analytics/AnalyticsPage"
import RadiologyPage from "../../pages/radiology/RadiologyPage"
import { getSession } from "../../app/state/appSessionStorage"

export enum RoutePaths {
  ROOT = "/",
  SIGN_IN = "/signin",
  SSO = "/sso",
  SETUP_MFA_INTRO = "/setup-mfa/intro",
  SETUP_MFA_TOTP = "/setup-mfa/totp",
  SETUP_MFA_TOTP_COMPLETE = "/setup-mfa/totp-complete",
  SETUP_MFA_SMS_COMPLETE = "/setup-mfa/sms-complete",
  MFA_TOTP = "/mfa/totp",
  TERMS = "/terms",
  SET_PASSWORD = "/set-password",
  MEETING_LIVE = "/meeting/:meetingId/live",
  MEETINGS = "/meetings",
  MEETING_NEW = "/meeting/new",
  MEETING_EDIT = "/meeting/:meetingId/edit",
  MEETING = "/meeting/:meetingId",
  MEETING_CASE = "/meetingcase/:meetingCaseId/:currentTab?",
  USERS = "/users",
  USER = "/user/:userId",
  USER_NEW = "/user/new",
  USER_NEW_GUEST = "/user/new_guest",
  USER_EDIT = "/user/:userId/edit",
  CASES = "/cases",
  CASE_NEW = "/case/new",
  CASE_EDIT = "/case/:caseId/edit",
  CASE_LOCK = "/case/:caseId/lock",
  CASE_UNLOCK = "/case/:caseId/unlock",
  CASE = "/case/:caseId/:currentTab?",
  PATIENTS = "/patients",
  PATIENT = "/patient/:patientId",
  PATIENT_NEW = "/patient/new",
  PATIENT_EDIT_DETAILS = "/patient/:patientId/details/edit",
  PATIENT_EDIT_FURTHER_DETAILS = "/patient/:patientId/further-details/edit",
  PATIENT_CASE_NEW = "/patient/:patientId/cases/new",
  RADIOLOGY = "/radiology",
  NOTIFICATIONS = "/notifications",
  TASKS = "/tasks",
  ADMIN = "/admin",
  TEAMS = "/teams",
  TEAMS_NEW = "/team/new",
  TEAM_EDIT = "/team/:teamId/edit",
  TEAM = "/team/:teamId",
  MESSAGES = "/messages",
  PATHWAYS = "/pathways",
  PATHWAY = "/pathway/:pathwayId",
  PATHWAY_NEW = "/pathway/new",
  ANALYTICS = "/analytics"
}

const blacklistDeepLinks: string[] = [
  RoutePaths.SIGN_IN,
  RoutePaths.SET_PASSWORD,
  RoutePaths.SETUP_MFA_INTRO,
  RoutePaths.SETUP_MFA_TOTP,
  RoutePaths.SETUP_MFA_TOTP_COMPLETE,
  RoutePaths.SETUP_MFA_SMS_COMPLETE,
  RoutePaths.MFA_TOTP
]

export const Routes = (): JSX.Element => {
  // preload all transations
  useTranslation(translationFilenames)

  const navigate = useNavigate()
  // the following is to support deep links while not signed in
  // initially storing the deep link should be done synchronously, as otherwise
  // the redirect to sign in has already taken place
  const [deepLinkPath, setDeepLinkPath] = useState<string>(null)
  const apiToken = useSelector((state: IAppState) => state.userSession.apiToken)
  const { oauthIdToken } = getSession()
  const currentUser = useSelector(getCurrentUser)
  if (!apiToken && !deepLinkPath) {
    setDeepLinkPath(location.pathname)
  }
  useEffect(() => {
    if (!apiToken || !oauthIdToken || !currentUser || currentUser.mfaRequired) return
    if (deepLinkPath && !blacklistDeepLinks.includes(deepLinkPath)) {
      navigate(deepLinkPath, { replace: true })
    }
    setDeepLinkPath(null)
  }, [apiToken, currentUser?.mfaRequired])

  return (
    <RouterRoutes>
      <Route path={RoutePaths.SIGN_IN} element={<AccountLayout><SignInPage /></AccountLayout>} />
      <Route path={RoutePaths.SSO} element={<AccountLayout><SignInPage /></AccountLayout>} />
      <Route path={RoutePaths.SET_PASSWORD} element={<AccountLayout><SetPasswordPage /></AccountLayout>} />

      <Route path={RoutePaths.TERMS} element={<TermsLayout><TermsPage /></TermsLayout>} />

      <Route path="/tenant/:tenantId/*" element={<AuthenticatedApp />} />
      <Route path="/*" element={<AuthenticatedApp />} />
    </RouterRoutes>
  )
}

const AuthenticatedApp: React.FC = () => {
  const currentUser = useSelector(getCurrentUser)
  const { tenantId } = useParams<"tenantId">()
  const matchedPath = tenantId ? `/tenant/${tenantId}` : ""

  const defaultRedirect =
    currentUser && isGuest(currentUser)
      ? <Navigate to={matchedPath + RoutePaths.CASES} replace />
      : <Navigate to={matchedPath + RoutePaths.MEETINGS} replace />

  return (
    <RouterRoutes>
      <Route index element={defaultRedirect} />
      <Route path="/" element={defaultRedirect} />

      <Route
        path={RoutePaths.MEETING_LIVE}
        element={<AuthenticatedRoute component={LiveMeetingPage} />}
      />

      <Route
        path={RoutePaths.MEETINGS}
        element={<AuthenticatedRoute component={MeetingsPage} layout={AppLayout} />}
      />
      <Route
        path={RoutePaths.MEETING_NEW}
        element={<AuthenticatedRoute component={NewMeetingPage} layout={AppLayout} />}
      />
      <Route
        path={RoutePaths.MEETING_EDIT}
        element={<AuthenticatedRoute component={EditMeetingPage} layout={AppLayout} />}
      />
      <Route
        path={RoutePaths.MEETING}
        element={<AuthenticatedRoute component={MeetingPage} layout={AppLayout} />}
      />
      <Route
        path={RoutePaths.RADIOLOGY}
        element={<AuthenticatedRoute component={RadiologyPage} layout={AppLayout} />}
      />
      <Route
        path={RoutePaths.MEETING_CASE}
        element={<AuthenticatedRoute component={MeetingCasePage} layout={AppLayout} />}
      />

      <Route
        path={RoutePaths.USERS}
        element={<AuthenticatedRoute component={UsersPage} layout={AppLayout} />}
      />

      <Route
        path={RoutePaths.USER_NEW}
        element={<AuthenticatedRoute component={UserNewPage} layout={AppLayout} />}
      />
      <Route
        path={RoutePaths.USER_NEW_GUEST}
        element={<AuthenticatedRoute component={UserNewGuestPage} layout={AppLayout} />}
      />

      <Route
        path={RoutePaths.USER_EDIT}
        element={<AuthenticatedRoute component={UserEditPage} />}
      />

      <Route
        path={RoutePaths.CASES}
        element={<AuthenticatedRoute component={CasesPage} layout={AppLayout} />}
      />
      <Route
        path={RoutePaths.CASE_EDIT}
        element={<AuthenticatedRoute component={CaseEditPage} layout={AppLayout} />}
      />
      <Route
        path={RoutePaths.CASE}
        element={<AuthenticatedRoute component={CasePage} layout={AppLayout} />}
      />
      <Route
        path={RoutePaths.NOTIFICATIONS}
        element={<AuthenticatedRoute component={NotificationsPage} layout={AppLayout} />}
      />
      <Route
        path={RoutePaths.TASKS}
        element={<AuthenticatedRoute component={TasksPage} layout={AppLayout} />}
      />
      <Route
        path={RoutePaths.PATIENTS}
        element={<AuthenticatedRoute component={PatientsPage} layout={AppLayout} />}
      />
      <Route
        path={RoutePaths.PATIENT_EDIT_DETAILS}
        element={<AuthenticatedRoute component={PatientEditDetailsPage} layout={AppLayout} />}
      />
      <Route
        path={RoutePaths.PATIENT_EDIT_FURTHER_DETAILS}
        element={<AuthenticatedRoute component={PatientEditFurtherDetailsPage} layout={AppLayout} />}
      />
      <Route
        path={RoutePaths.PATIENT_CASE_NEW}
        element={<AuthenticatedRoute component={PatientCaseNewPage} layout={AppLayout} />}
      />
      <Route
        path={RoutePaths.PATIENT_NEW}
        element={<AuthenticatedRoute component={PatientNewPage} layout={AppLayout} />}
      />
      <Route
        path={RoutePaths.PATIENT}
        element={<AuthenticatedRoute component={PatientPage} layout={AppLayout} />}
      />

      <Route
        path={RoutePaths.PATHWAYS}
        element={<AuthenticatedRoute component={PathwaysPage} layout={AppLayout} />}
      />

      <Route
        path={RoutePaths.PATHWAY_NEW}
        element={<AuthenticatedRoute component={PathwayNewPage} layout={AppLayout} />}
      />

      <Route
        path={RoutePaths.PATHWAY}
        element={<AuthenticatedRoute component={PathwayPage} layout={AppLayout} />}
      />

      <Route
        path={RoutePaths.ADMIN}
        element={<AuthenticatedRoute component={AdminPage} layout={AppLayout} />}
      />

      <Route
        path={RoutePaths.ANALYTICS}
        element={<AuthenticatedRoute component={AnalyticsPage} layout={AppLayout} />}
      />

      <Route
        path={RoutePaths.SETUP_MFA_INTRO}
        element={<AuthenticatedRoute component={() => <MfaPage directRoute />} layout={AccountLayout} overrideMfaIntercept />}
      />

      <Route
        path={RoutePaths.SETUP_MFA_TOTP_COMPLETE}
        element={<AuthenticatedRoute component={SetupTotpCompletePage} layout={AccountLayout} overrideMfaIntercept />}
      />

      <Route
        path={RoutePaths.SETUP_MFA_SMS_COMPLETE}
        element={<AuthenticatedRoute component={SetupSmsCompletePage} layout={AccountLayout} overrideMfaIntercept />}
      />

      <Route
        path={RoutePaths.MFA_TOTP}
        element={<AuthenticatedRoute component={MfaTotpPage} layout={AccountLayout} />}
      />

      <Route
        path={RoutePaths.TEAMS}
        element={<AuthenticatedRoute component={TeamsPage} layout={AppLayout} />}
      />

      <Route
        path={RoutePaths.TEAM_EDIT}
        element={<AuthenticatedRoute component={TeamEditPage} layout={AppLayout} />}
      />

      <Route
        path={RoutePaths.TEAMS_NEW}
        element={<AuthenticatedRoute component={TeamsNewPage} layout={AppLayout} />}
      />

      <Route
        path={RoutePaths.TEAM}
        element={<AuthenticatedRoute component={TeamPage} layout={AppLayout} />}
      />

      <Route
        path={RoutePaths.MESSAGES}
        element={<AuthenticatedRoute component={MessagesPage} layout={AppLayout} />}
      />
    </RouterRoutes>
  )
}
